import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

interface LinkTileProps {
  /** Where the tile goes. */
  to: string;
  title: ReactNode;
  /** Full title on hover, for one that gets truncated. */
  titleTooltip?: string;
  /** Right of the title: a delta chip, a small status. */
  adornment?: ReactNode;
  /** One grey line under the title. */
  caption?: ReactNode;
  /** A row under the title: avatars, a sparkline, a count pushed right. */
  meta?: ReactNode;
  /** Greyed out (an archived list). */
  muted?: boolean;
  /** Only the title, centred in primary: a "+3 more" tile closing a row of tiles. */
  more?: boolean;
}

/** A small outlined, clickable tile in a grid of things to open (lists on the dashboard). */
export default function LinkTile({
  to,
  title,
  titleTooltip,
  adornment,
  caption,
  meta,
  muted = false,
  more = false,
}: LinkTileProps) {
  const heading = (
    <Typography
      variant="body2"
      noWrap={!more}
      sx={{ fontWeight: 600, flex: more ? undefined : 1, minWidth: 0, color: more ? "primary.main" : undefined }}
    >
      {title}
    </Typography>
  );
  return (
    <Card sx={{ opacity: muted ? 0.6 : 1, minWidth: 0 }}>
      <CardActionArea
        component={RouterLink}
        to={to}
        sx={{
          height: "100%",
          p: 1.25,
          ...(more && { display: "flex", alignItems: "center", justifyContent: "center" }),
        }}
      >
        {more ? (
          heading
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              {titleTooltip ? <Tooltip title={titleTooltip}>{heading}</Tooltip> : heading}
              {adornment}
            </Box>
            {caption != null && (
              <Typography variant="caption" noWrap component="div" sx={{ color: "text.secondary" }}>
                {caption}
              </Typography>
            )}
            {meta != null && (
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mt: 0.75 }}>
                {meta}
              </Box>
            )}
          </>
        )}
      </CardActionArea>
    </Card>
  );
}
